import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type ApiProductRow = {
  id: number;
  main_title: string | null;
  variant_name: string | null;
  sku: string | null;
  brand_id: number | null;
  brand_name: string | null;
};

type ApiBrandRow = {
  id: number;
  brand_name: string;
};

type ManagementResponse = {
  count: number;
  page: number;
  page_size: number;
  results: ApiProductRow[];
};

function parseSkusFromText(text: string): string[] {
  const raw = String(text || '')
    .split(/[\r\n,\t ]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  // Stable dedup
  return Array.from(new Set(raw));
}

export function ProductManagementPage() {
  const [q, setQ] = useState('');
  const [uncategorizedOnly, setUncategorizedOnly] = useState(false);

  const [pageSize, setPageSize] = useState('25');
  const [page, setPage] = useState(1);

  const [brands, setBrands] = useState<ApiBrandRow[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ManagementResponse>({ count: 0, page: 1, page_size: 25, results: [] });

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkBrandId, setBulkBrandId] = useState<number | null | undefined>(undefined);
  const [bulkBrandSearch, setBulkBrandSearch] = useState('');
  const [applying, setApplying] = useState(false);

  const [skuDialogOpen, setSkuDialogOpen] = useState(false);
  const [skuText, setSkuText] = useState('');
  const [skuMode, setSkuMode] = useState(false);
  const [lastSkuList, setLastSkuList] = useState<string[]>([]);

  const visibleRows = data.results || [];

  const allSelectedOnPage = useMemo(() => {
    if (visibleRows.length === 0) return false;
    return visibleRows.every((r) => selectedIds.has(r.id));
  }, [visibleRows, selectedIds]);

  const someSelectedOnPage = useMemo(() => {
    if (visibleRows.length === 0) return false;
    return visibleRows.some((r) => selectedIds.has(r.id));
  }, [visibleRows, selectedIds]);

  const filteredBrands = useMemo(() => {
    const s = bulkBrandSearch.trim().toLowerCase();
    if (!s) return brands;
    return brands.filter((b) => (b.brand_name || '').toLowerCase().includes(s));
  }, [brands, bulkBrandSearch]);

  async function loadBrands() {
    setBrandsLoading(true);
    try {
      const resp = await fetch('/v1/api/gp-reporter/brands/', { method: 'GET' });
      if (!resp.ok) return;
      const raw = (await resp.json()) as unknown;
      const arr = Array.isArray(raw) ? raw : [];
      const parsed = arr
        .map((x) => {
          const id = Number((x as any)?.id);
          const brand_name = String((x as any)?.brand_name ?? '').trim();
          if (!Number.isFinite(id) || !brand_name) return null;
          return { id, brand_name };
        })
        .filter(Boolean) as ApiBrandRow[];
      setBrands(parsed);
    } finally {
      setBrandsLoading(false);
    }
  }

  async function loadList(params?: { nextPage?: number }) {
    const nextPage = params?.nextPage ?? page;
    const ps = Number(pageSize);
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (q.trim()) qs.set('q', q.trim());
      if (uncategorizedOnly) qs.set('uncategorized', 'true');
      qs.set('page', String(nextPage));
      qs.set('page_size', String(Number.isFinite(ps) ? ps : 25));

      const resp = await fetch(`/v1/api/gp-reporter/products/management/?${qs.toString()}`, { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const json = (await resp.json()) as unknown;
      const parsed = json as ManagementResponse;
      setData({
        count: Number((parsed as any)?.count ?? 0) || 0,
        page: Number((parsed as any)?.page ?? nextPage) || nextPage,
        page_size: Number((parsed as any)?.page_size ?? ps) || ps,
        results: Array.isArray((parsed as any)?.results) ? ((parsed as any).results as ApiProductRow[]) : [],
      });
      setPage(nextPage);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  }

  async function loadBySkus(skus: string[]) {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/products/by-skus/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skus, limit: 5000 }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      const arr = Array.isArray(raw) ? raw : [];
      const results = arr
        .map((x) => {
          const id = Number((x as any)?.id);
          if (!Number.isFinite(id)) return null;
          return {
            id,
            main_title: (x as any)?.main_title ?? null,
            variant_name: (x as any)?.variant_name ?? null,
            sku: (x as any)?.sku ?? null,
            brand_id: (x as any)?.brand_id ?? null,
            brand_name: (x as any)?.brand_name ?? null,
          } as ApiProductRow;
        })
        .filter(Boolean) as ApiProductRow[];

      setSkuMode(true);
      setLastSkuList(skus);
      setData({ count: results.length, page: 1, page_size: results.length, results });
      setSelectedIds(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to lookup SKUs.');
    } finally {
      setLoading(false);
    }
  }

  async function applyBulkBrand() {
    if (applying) return;
    if (bulkBrandId === undefined) {
      setError('Please select a brand to apply.');
      return;
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setApplying(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/products/bulk-update-brand-id/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: bulkBrandId, product_ids: ids }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      setSelectedIds(new Set());
      // Refresh data to reflect new brand names
      if (skuMode) {
        await loadBySkus(lastSkuList);
      } else {
        await loadList();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update products.');
    } finally {
      setApplying(false);
    }
  }

  async function setRowBrand(productId: number, brandId: number | null) {
    // Optimistic update
    const prev = visibleRows.find((r) => r.id === productId);
    const nextBrandName = brandId === null ? null : brands.find((b) => b.id === brandId)?.brand_name ?? null;
    setData((cur) => ({
      ...cur,
      results: (cur.results || []).map((r) =>
        r.id === productId ? { ...r, brand_id: brandId, brand_name: nextBrandName } : r
      ),
    }));

    try {
      const resp = await fetch('/v1/api/gp-reporter/products/bulk-update-brand-id/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: brandId, product_ids: [productId] }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
    } catch (e) {
      // rollback
      if (prev) {
        setData((cur) => ({
          ...cur,
          results: (cur.results || []).map((r) => (r.id === productId ? prev : r)),
        }));
      }
      setError(e instanceof Error ? e.message : 'Failed to update brand.');
    }
  }

  useEffect(() => {
    void loadBrands();
    void loadList({ nextPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search reload (disabled in SKU mode)
  useEffect(() => {
    if (skuMode) return;
    const handle = setTimeout(() => {
      void loadList({ nextPage: 1 });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, uncategorizedOnly, pageSize, skuMode]);

  const pageStart = useMemo(() => {
    if (data.count === 0) return 0;
    return (page - 1) * Number(pageSize) + 1;
  }, [data.count, page, pageSize]);

  const pageEnd = useMemo(() => {
    if (data.count === 0) return 0;
    return Math.min(page * Number(pageSize), data.count);
  }, [data.count, page, pageSize]);

  const bulkBarVisible = selectedIds.size > 0;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-card text-card-foreground">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by main title, variant name, or SKU..."
                value={q}
                onChange={(e) => setQ(e.currentTarget.value)}
                className="pl-9"
                disabled={skuMode}
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="uncategorized-only"
                  checked={uncategorizedOnly}
                  onCheckedChange={(v) => setUncategorizedOnly(Boolean(v))}
                  disabled={skuMode}
                />
                <Label htmlFor="uncategorized-only" className="text-sm font-normal text-muted-foreground">
                  Uncategorized only
                </Label>
              </div>

              <Button variant="outline" onClick={() => setSkuDialogOpen(true)} disabled={loading} className="font-normal">
                Paste SKU list
              </Button>

              {skuMode && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSkuMode(false);
                    setLastSkuList([]);
                    void loadList({ nextPage: 1 });
                  }}
                  disabled={loading}
                  className="font-normal"
                >
                  Back to list
                </Button>
              )}

              <Select
                value={pageSize}
                onValueChange={(v) => {
                  setPageSize(v);
                  setPage(1);
                }}
                disabled={skuMode}
              >
                <SelectTrigger className="w-[130px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {bulkBarVisible && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-md border p-3 bg-muted/20">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{selectedIds.size}</span> selected
              </div>

              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="h-9 w-[260px] justify-between font-normal" disabled={brandsLoading}>
                      <span className="truncate">
                        {bulkBrandId === undefined
                          ? 'Select brand'
                          : bulkBrandId === null
                            ? 'Uncategorized (clear)'
                            : brands.find((b) => b.id === bulkBrandId)?.brand_name ?? 'Select brand'}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[340px] max-w-[80vw]" align="end">
                    <DropdownMenuLabel>Apply brand to selected</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <div className="p-2">
                      <Input
                        placeholder="Search brand..."
                        value={bulkBrandSearch}
                        onChange={(e) => setBulkBrandSearch(e.currentTarget.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <DropdownMenuSeparator />
                    <div className="max-h-64 overflow-auto">
                      <DropdownMenuItem onSelect={() => setBulkBrandId(null)}>Uncategorized (clear)</DropdownMenuItem>
                      {filteredBrands.slice(0, 200).map((b) => (
                        <DropdownMenuItem key={b.id} onSelect={() => setBulkBrandId(b.id)}>
                          {b.brand_name}
                        </DropdownMenuItem>
                      ))}
                      {filteredBrands.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No brands found.</div>
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button onClick={applyBulkBrand} disabled={applying || bulkBrandId === undefined} className="h-9">
                  {applying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Apply
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {skuMode ? (
                <>
                  Showing <span className="font-medium text-foreground">{data.count}</span> results (SKU list)
                </>
              ) : (
                <>
                  Showing <span className="font-medium text-foreground">{pageStart}</span> to{' '}
                  <span className="font-medium text-foreground">{pageEnd}</span> of{' '}
                  <span className="font-medium text-foreground">{data.count}</span>
                </>
              )}
            </div>

            {!skuMode && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="h-9 font-normal"
                  onClick={() => void loadList({ nextPage: Math.max(1, page - 1) })}
                  disabled={loading || page <= 1}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  className="h-9 font-normal"
                  onClick={() => void loadList({ nextPage: page + 1 })}
                  disabled={loading || pageEnd >= data.count}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{error}</span>
            </div>
          )}

          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[44px]">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      ref={(el) => {
                        if (!el) return;
                        el.indeterminate = !allSelectedOnPage && someSelectedOnPage;
                      }}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        setSelectedIds((cur) => {
                          const next = new Set(cur);
                          if (checked) visibleRows.forEach((r) => next.add(r.id));
                          else visibleRows.forEach((r) => next.delete(r.id));
                          return next;
                        });
                      }}
                      aria-label="Select all on page"
                    />
                  </TableHead>
                  <TableHead>Main Title</TableHead>
                  <TableHead>Variant Name</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="w-[220px]">Brand Name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((r) => {
                    const checked = selectedIds.has(r.id);
                    return (
                      <TableRow key={r.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const isChecked = e.currentTarget.checked;
                              setSelectedIds((cur) => {
                                const next = new Set(cur);
                                if (isChecked) next.add(r.id);
                                else next.delete(r.id);
                                return next;
                              });
                            }}
                            aria-label={`Select product ${r.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{String(r.main_title ?? '')}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{String(r.variant_name ?? '')}</TableCell>
                        <TableCell className="text-sm font-mono">{String(r.sku ?? '')}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" className="h-8 w-full justify-between font-normal" disabled={brandsLoading}>
                                <span className="truncate">
                                  {r.brand_name ? r.brand_name : <span className="text-muted-foreground">Uncategorized</span>}
                                </span>
                                <ChevronDown className="h-4 w-4 opacity-60" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-[260px]" align="end">
                              <DropdownMenuItem onSelect={() => void setRowBrand(r.id, null)}>
                                Uncategorized (clear)
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {brands.slice(0, 200).map((b) => (
                                <DropdownMenuItem key={b.id} onSelect={() => void setRowBrand(r.id, b.id)}>
                                  {b.brand_name}
                                </DropdownMenuItem>
                              ))}
                              {brands.length === 0 && (
                                <div className="px-3 py-2 text-sm text-muted-foreground">No brands loaded.</div>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog open={skuDialogOpen} onOpenChange={setSkuDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Paste SKU list</DialogTitle>
            <DialogDescription>Paste SKUs from Excel (one per line, comma, or space separated).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="sku-text">SKUs</Label>
            <textarea
              id="sku-text"
              value={skuText}
              onChange={(e) => setSkuText(e.currentTarget.value)}
              placeholder={`TP-12345-RED-M\nTP-12345-RED-L\n...`}
              className="min-h-[200px] w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {(() => {
                  const skus = parseSkusFromText(skuText);
                  return skus.length > 0 ? `${skus.length} SKU(s) detected` : '0 SKU';
                })()}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="font-normal" onClick={() => setSkuDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    const skus = parseSkusFromText(skuText);
                    if (skus.length === 0) return;
                    setSkuDialogOpen(false);
                    await loadBySkus(skus);
                  }}
                >
                  Lookup
                </Button>
              </div>
            </div>
            <div className="pt-1">
              <Badge variant="outline" className="font-normal">
                Tip: you can bulk-select all results and apply a brand once.
              </Badge>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

